/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/hooks/useUserTracking.ts
 *
 * Rôle :
 *   Hook + service de tracking parcours visiteur. Dispatch des events
 *   (page_view, route_change, badge_show, media_play, workshop_view,
 *   stand_view, search_query, click_cta) vers `/api/events.php` par
 *   batch toutes les 30 secondes (économie réseau et batterie iOS).
 *
 * Pipeline :
 *   1. Composants appellent `dispatchUserEvent({ kind, resourceId, … })`
 *   2. Event est enqueued dans une queue locale en mémoire (RAM, max 200)
 *   3. Toutes les 30s, drain via `apiMutate('events.php', ...)`
 *      → si offline, l'enqueue offline (§18) prend le relais
 *   4. Au unmount complet (close tab / app), envoi final via `sendBeacon`
 *
 * Garde-fous :
 *   - RGPD §10.10 : pas de PII dans le payload (jamais d'email/nom dans
 *     event.resource_id — on stocke des slugs opaques).
 *   - Mineurs : si `is_minor=1` (lu depuis localStorage profil) →
 *     tracking automatiquement DÉSACTIVÉ (RGPD art. 8 + §10.16.7).
 *   - Master switch `ai.user_tracking` côté serveur — peut être
 *     désactivé sans rebuild.
 *   - Anti-spam : throttle 500ms par (kind, resourceId) — pas 2x le même
 *     event en 500ms.
 *   - Anti-double-page-view : dedup par route + timestamp (max 1
 *     page_view par route en 5 sec).
 *
 * Voir aussi : CLAUDE.md §5 entrée 2026-05-11 (récup NOLAS) + §10.10
 * (RGPD tracking) + §14.11 (tracking temps réel) + §F15 (tracking
 * extrêmement performant — demande explicite Mika 2026-05-11).
 *
 * @author Mika
 * @since 2026-05-11
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiMutate } from '@/api/client';

// ──────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────

/**
 * Catégorie d'event utilisateur. Aligné sur `tne_user_events.event_kind`
 * (cf. V17 SQL).
 */
export type UserEventKind =
    | 'page_view'        // Navigation sur une route
    | 'route_change'     // Changement de route via React Router
    | 'badge_show'       // Ouverture de la page Mon badge
    | 'badge_print'      // Clic « Imprimer A6 »
    | 'media_play'       // Lecture d'un média (audio/vidéo)
    | 'media_pause'      // Pause / arrêt
    | 'workshop_view'    // Consultation détail atelier
    | 'workshop_signup'  // Inscription atelier
    | 'workshop_cancel'  // Désinscription
    | 'stand_view'       // Clic sur un stand sur la carte
    | 'partner_view'     // Consultation fiche partenaire
    | 'search_query'     // Recherche dans l'app
    | 'click_cta'        // Clic sur un CTA principal
    | 'scan_qr'          // Scan QR depuis l'app (badge)
    | 'install_prompt'   // Affichage du prompt d'install PWA
    | 'install_accepted' // Acceptation install PWA
    | 'phase_change'     // Bascule before → during → after
    | 'session_start'    // Ouverture de l'app
    | 'session_end';     // Fermeture (sendBeacon)

export interface UserEvent {
    readonly kind: UserEventKind;
    /** Ressource ciblée (slug, ID opaque — JAMAIS de PII). */
    readonly resourceId?: string;
    /** Type de ressource (workshop, partner, media, stand, page, …). */
    readonly resourceKind?: string;
    /** Durée passée sur la ressource en secondes (pour page_view). */
    readonly dwellSeconds?: number;
    /** Métadonnées libres (max 200 chars sérialisées). */
    readonly meta?: Record<string, string | number | boolean>;
}

interface QueuedEvent extends UserEvent {
    readonly clientTimestamp: number;
}

// ──────────────────────────────────────────────────────────────────────────
// État global du tracker (singleton module-level, ne fuit pas en tests)
// ──────────────────────────────────────────────────────────────────────────

const QUEUE_MAX_SIZE = 200;
const DRAIN_INTERVAL_MS = 30_000;
const THROTTLE_MS = 500;
const PAGE_VIEW_DEDUP_MS = 5_000;

let queue: QueuedEvent[] = [];
const lastDispatchByKey: Map<string, number> = new Map();
let lastPageView: { route: string; ts: number } | null = null;
let drainTimer: ReturnType<typeof setInterval> | null = null;
let trackingEnabled = true; // peut être basculé par opt-out

/**
 * Désactive globalement le tracking (cas mineur RGPD ou opt-out user).
 */
export function disableUserTracking(): void {
    trackingEnabled = false;
    queue = []; // purge ce qui restait
}

/**
 * Réactive le tracking (cas où l'utilisateur change d'avis depuis le
 * panneau RGPD).
 */
export function enableUserTracking(): void {
    trackingEnabled = true;
}

export function isUserTrackingEnabled(): boolean {
    return trackingEnabled;
}

/**
 * Dispatch un event. Appelable depuis n'importe quel composant.
 *
 * @example
 *   dispatchUserEvent({ kind: 'workshop_view', resourceId: 'atelier-ia-college', resourceKind: 'workshop' });
 */
export function dispatchUserEvent(event: UserEvent): void {
    if (!trackingEnabled) return;

    // Mineur ? On vérifie le profil cached en localStorage (cf. RGPD)
    try {
        const cached = window.localStorage.getItem('tne.profile');
        if (cached) {
            const profile = JSON.parse(cached) as { is_minor?: boolean };
            if (profile.is_minor === true) {
                return; // tracking désactivé pour mineurs §10.16.7
            }
        }
    } catch {
        // localStorage indispo — on accepte le tracking (event sans dimension mineur)
    }

    // Throttle par (kind, resourceId)
    const key = `${event.kind}:${event.resourceId ?? '_'}`;
    const now = Date.now();
    const last = lastDispatchByKey.get(key);
    if (last !== undefined && now - last < THROTTLE_MS) {
        return;
    }
    lastDispatchByKey.set(key, now);

    // Plafond queue
    if (queue.length >= QUEUE_MAX_SIZE) {
        // Drain immédiat pour éviter de perdre des events
        void drainNow();
        if (queue.length >= QUEUE_MAX_SIZE) {
            // On garde les derniers (drop les plus anciens)
            queue.shift();
        }
    }

    queue.push({ ...event, clientTimestamp: now });
}

/**
 * Drain manuel — utile au sendBeacon de fin de session.
 */
async function drainNow(): Promise<void> {
    if (queue.length === 0) return;
    const batch = queue.splice(0, queue.length);
    try {
        await apiMutate(
            'events.php',
            'POST',
            { events: batch },
            { kind: 'tracking.events.batch' },
        );
    } catch {
        // En cas d'échec, on remet les events en tête de queue pour
        // retry au prochain drain (sauf si > QUEUE_MAX_SIZE).
        if (queue.length + batch.length <= QUEUE_MAX_SIZE) {
            queue = [...batch, ...queue];
        }
    }
}

/**
 * Démarre le drainer périodique. Idempotent — un seul timer global.
 */
export function startUserTrackingDrainer(): void {
    if (drainTimer !== null) return;
    drainTimer = setInterval(() => {
        void drainNow();
    }, DRAIN_INTERVAL_MS);

    // sendBeacon au unmount complet (close tab / refresh)
    if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', () => {
            if (queue.length === 0) return;
            try {
                const blob = new Blob(
                    [JSON.stringify({ events: queue })],
                    { type: 'application/json' },
                );
                // sendBeacon : envoi best-effort qui ne bloque pas l'unload
                navigator.sendBeacon('/api/events.php', blob);
                queue = [];
            } catch {
                // sendBeacon indispo (rare) → on perd ces events, pas grave
            }
        });
    }
}

/**
 * Stoppe le drainer (utile aux tests pour cleanup).
 */
export function stopUserTrackingDrainer(): void {
    if (drainTimer !== null) {
        clearInterval(drainTimer);
        drainTimer = null;
    }
}

// ──────────────────────────────────────────────────────────────────────────
// Hook React — auto-dispatch page_view sur changement de route
// ──────────────────────────────────────────────────────────────────────────

/**
 * Hook à monter UNE seule fois (typiquement dans AppLayout). Détecte
 * les changements de route React Router et dispatch automatiquement
 * un `route_change` + `page_view` (avec dedup 5s par route pour éviter
 * les re-mounts inutiles).
 */
export function useUserTracking(): void {
    const location = useLocation();
    const previousPathRef = useRef<string | null>(null);
    const enterTsRef = useRef<number>(Date.now());

    useEffect(() => {
        const currentPath = location.pathname;
        const now = Date.now();

        // Dwell sur la route précédente
        if (previousPathRef.current !== null && previousPathRef.current !== currentPath) {
            const dwell = Math.round((now - enterTsRef.current) / 1000);
            // On dispatch en arrière le dwell de la route quittée
            dispatchUserEvent({
                kind: 'route_change',
                resourceId: previousPathRef.current,
                resourceKind: 'route',
                dwellSeconds: dwell,
            });
        }

        // Dedup page_view (max 1 par route en 5 sec)
        if (
            lastPageView === null ||
            lastPageView.route !== currentPath ||
            now - lastPageView.ts > PAGE_VIEW_DEDUP_MS
        ) {
            dispatchUserEvent({
                kind: 'page_view',
                resourceId: currentPath,
                resourceKind: 'route',
            });
            lastPageView = { route: currentPath, ts: now };
        }

        previousPathRef.current = currentPath;
        enterTsRef.current = now;
    }, [location.pathname]);

    // Démarrage du drainer au premier mount
    useEffect(() => {
        startUserTrackingDrainer();
        dispatchUserEvent({ kind: 'session_start', resourceKind: 'app' });
        // Pas de cleanup du drainer ici — il vit toute la durée de la session.
    }, []);
}

// ──────────────────────────────────────────────────────────────────────────
// Notes pour rétro-port NOLAS :
//   NOLAS Tracking n'avait PAS de tracking comportemental (juste les
//   check-ins). Ce module est entièrement nouveau et n'a pas
//   d'équivalent NOLAS à porter.
//   Il peut être rétro-porté dans NOLAS modernisé en réécrivant le
//   useLocation() → window.location + popstate listener.
// ──────────────────────────────────────────────────────────────────────────
